import { doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { documentTotals, type DocumentLine } from '@finvox/shared/invoice-math';
import { parseMoneyInput } from '@finvox/shared/money';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { usePaginatedPartySelect } from '@/hooks/usePaginatedPartySelect';
import { Pagination } from '@/components/ui/Pagination';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

export function PurchaseBillPage() {
  const { t } = useTranslation();
  const { billId } = useParams<{ billId: string }>();
  const { orgId, skipLiveUpdate } = useOrg();
  const online = useOnlineStatus();
  const vendors = usePaginatedPartySelect(orgId, 'vendors');
  const [bill, setBill] = useState<Record<string, unknown> | null>(null);
  const [lines, setLines] = useState<DocumentLine[]>([]);

  useEffect(() => {
    if (!orgId || !billId) return;
    return onSnapshot(doc(getFirestoreDb(), 'organizations', orgId, 'purchaseBills', billId), (snap) => {
      if (skipLiveUpdate(snap.metadata, orgId)) return;
      const d = snap.data();
      if (!d) return;
      setBill(d);
      setLines((d.lines ?? []) as DocumentLine[]);
    });
  }, [orgId, billId, skipLiveUpdate]);

  const totals = useMemo(() => documentTotals(lines), [lines]);
  const readonly = bill?.status === 'posted';

  async function save(patch: Record<string, unknown>) {
    if (!orgId || !billId || !online || readonly) return;
    await updateDoc(doc(getFirestoreDb(), 'organizations', orgId, 'purchaseBills', billId), {
      ...patch,
      updatedAt: serverTimestamp()
    });
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('purchases.bill')}</h1>
      <Select
        className="mt-4 w-full max-w-md"
        disabled={readonly}
        value={String(bill?.vendorId ?? '')}
        onValueChange={(id) => void save({ vendorId: id })}
        options={vendors.parties.map((v) => ({
          value: v.id,
          label: `${v.code} — ${v.name}`
        }))}
      />
      <Pagination
        className="mt-2 max-w-md border-0 px-0"
        pageIndex={vendors.pageIndex}
        hasNext={vendors.hasNext}
        hasPrev={vendors.hasPrev}
        onNext={vendors.nextPage}
        onPrev={vendors.prevPage}
        loading={vendors.loading}
      />
      {lines.map((line, i) => (
        <div key={i} className="mt-2 flex gap-2">
          <input
            className="w-28 rounded border px-2 py-1"
            disabled={readonly}
            value={line.unitPrice}
            onChange={(e) => {
              const next = [...lines];
              next[i] = { ...line, unitPrice: parseMoneyInput(e.target.value) };
              setLines(next);
              void save({ lines: next });
            }}
          />
        </div>
      ))}
      <p className="mt-4 font-semibold">
        {t('sales.total')}: <MoneyDisplay value={totals.gross} className="font-semibold" />
      </p>
      <label className="mt-3 flex max-w-xs flex-col gap-1 text-sm">
        {t('purchases.withholding')}
        <input
          className="rounded border px-3 py-2 tabular-nums"
          disabled={readonly}
          value={String(bill?.withholdingTotal ?? '0.00')}
          onChange={(e) => void save({ withholdingTotal: e.target.value })}
        />
      </label>
      {!readonly && (
        <Button
          className="mt-4"
          disabled={!online}
          onClick={() => void apiPost('/v1/purchase-bills/post', { orgId, billId })}
        >
          {t('purchases.postBill')}
        </Button>
      )}
    </div>
  );
}
